// Ensure global indexedDB exists to prevent ReferenceErrors in environments like Safari Lockdown Mode
if (typeof window !== 'undefined' && !('indexedDB' in window)) {
  (window as any).indexedDB = null;
}

import * as React from 'react';
import * as ReactDOMClient from 'react-dom/client';
import * as UserRepository from './Repository/UserRepository';
import * as ContractRepository from './Repository/ContractRepository';
import * as AuthService from "./Service/AuthService";
import { Modal } from 'antd';
import { featureFlags } from "./Service/FeatureFlags";
import { BrowserRouter } from "react-router-dom";
import { Workbox } from "workbox-window";
import { User } from 'tree-wallet-client';
import ConfigurationService from "./Service/ConfigurationService";
import App from "./App";
import mapboxgl from "mapbox-gl";

import '../assets/css/mapbox-gl-2.11.css';
import '../assets/less/main.less';

// mapbox-gl sends two telemetry beacons trackers as soon as an access token is set: 
// `https://events.mapbox.com/events/v2` and `https://api.mapbox.com/map-sessions/v1`.
// Both are gated on `config.EVENTS_URL`, which is a *getter* derived from `config.API_URL` -
// a plain assignment to it is silently ignored, so the property is redefined here instead.
// `API_URL` is untouched, so styles, tiles, sprites, fonts and terrain keep working as before.
Object.defineProperty(mapboxgl.config, 'EVENTS_URL', { value: null, configurable: true, writable: true });

// Desktop page zoom: a trackpad pinch arrives as a `wheel` event carrying `ctrlKey` (Chrome,
// Firefox) or as the `gesture*` events (Safari), and the browser zooms the whole page in response -
// leaving the user zoomed into a page that cannot be panned back. Those defaults are cancelled here,
// which is what the viewport meta in `index-template.html` already does on mobile.
// Gestures that start on the map are left alone: Mapbox turns them into a map zoom of its own. Same
// for the full screen photo viewer, which zooms with its own code. Only the browser's default action
// is cancelled, so both keep working; and the browser's own zoom (the `Ctrl`/`Cmd` shortcuts) is
// deliberately left available.
const isInsideMap = (target: EventTarget | null) =>
  target instanceof Element && target.closest('.mapboxgl-map') !== null;

const preventPageZoom = (event: Event) => {
  if (!isInsideMap(event.target)) {
    event.preventDefault();
  }
};

// `passive: false` is required, `preventDefault()` is ignored on a passive wheel listener.
window.addEventListener('wheel', (event: WheelEvent) => {
  // A trackpad pinch reports itself as a wheel with `ctrlKey`, like `Ctrl` + wheel with a mouse.
  if (event.ctrlKey) preventPageZoom(event);
}, { passive: false });

// Safari's trackpad pinch events. Captured on the window so they are caught wherever they are
// dispatched from and whether or not they bubble.
['gesturestart', 'gesturechange', 'gestureend'].forEach((type) => {
  window.addEventListener(type, preventPageZoom, { passive: false, capture: true });
});

let modal: ReturnType<typeof Modal.info> | null = null;

function onError(message: any, level: string) {
  try {
    const maxSize = 150;
    const errorsJson = window.localStorage.getItem('errors');
    const errors = JSON.parse(errorsJson ? errorsJson : '[]');

    let messageStr = '';
    if (message instanceof Error) {
      messageStr = message.stack || message.message || String(message);
    } else if (typeof message === 'object' && message !== null) {
      try {
        messageStr = JSON.stringify(message);
      } catch (e) {
        messageStr = String(message);
      }
    } else {
      messageStr = String(message);
    }

    // Limit individual log size to 1000 characters to prevent using up localStorage quota
    if (messageStr.length > 1000) {
      messageStr = messageStr.substring(0, 1000) + '... [TRUNCATED]';
    }

    errors.push({
      level: level,
      message: messageStr,
      timestamp: new Date().getTime()
    });

    try {
      window.localStorage.setItem('errors', JSON.stringify(errors.slice(-maxSize)));
    } catch (quotaError) {
      // Storage is full — clear the error log and try again with only the latest entry
      try {
        window.localStorage.removeItem('errors');
        window.localStorage.setItem('errors', JSON.stringify([{
          level: level,
          message: messageStr,
          timestamp: new Date().getTime()
        }]));
      } catch (e) {
        // Silently suppress if still failing — never crash the app over logging
      }
    }
  } catch (err) {
    // Safe fallback: Suppress all storage exceptions (like QuotaExceededError)
    // to prevent application crashes during console.log calls.
  }
}

try {
  // The service worker is a *build* thing: test, preprod and production register it, a local dev server does not. 
  // Locally it would serve the app from whatever `public/service-worker.js` a previous build left on disk,
  // precache included, instead of the dev server's own output. webpack's `mode` is what tells them apart -
  // NODE_ENV is `production` for every build and `development` for `npm start` / `watch-dev`.
  // Don't be misled by .gitlab-ci.yml: its `NODE_ENV=development` / `NODE_ENV=prod` shell variables steer
  // build tooling only. The bundled value comes from webpack's `optimization.nodeEnv`, which defaults to
  // `mode` - and both deploy configs set `mode: 'production'` - so this is true on every deployed environment.
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const wb = new Workbox('/service-worker.js');

    // Persist the currently running app version once the SW is activated
    // This helps differentiate first installs from subsequent updates
    wb.addEventListener('activated', () => {
      try {
        ConfigurationService.setCurrentVersion();
      } catch (e) {
        console.error('Failed to set current version after SW activation', e);
      }
    });

    // Track whether this is the first-ever SW install (not an update)
    // Initialize from localStorage to avoid race with BroadcastChannel messages
    let isFirstSWInstall = !ConfigurationService.isCurrentVersionInstalled();
    wb.addEventListener('installed', (event: any) => {
      // event.isUpdate is true when a new SW has been installed over an existing one
      isFirstSWInstall = !(event && event.isUpdate);
    });

    const broadcast = new BroadcastChannel('sw-update-channel');
    broadcast.onmessage = (event) => {
      console.log('[SW] received messages from broadcast channel', event);

      // Do not prompt on first-ever install; allow prompts on real updates
      if (event.data && event.data.type && featureFlags.displayUpdateModal === true && !isFirstSWInstall) {
        switch (event.data.type) {
          case 'CRITICAL_SW_UPDATE_INSTALLING':
            if (modal) modal.destroy();
            modal = Modal.info({
              title: 'Update found',
              content: (
                <div>
                  <p>An update has been found and is being installed</p>
                </div>
              ),
              centered: true,
              okText: 'Please wait',
              okButtonProps: { loading: true },
            });
            break;
          case 'CRITICAL_SW_UPDATE_ACTIVATING':
            console.log('[SW] updating modal')
            if (modal) modal.destroy();
            modal = Modal.success({
              centered: true,
              okButtonProps: { loading: false },
              okText: 'OK',
              title: 'Update installed',
              content: (
                <div>
                  <p>Please click OK to reload the page and access the app</p>
                </div>
              ),
              onOk() {
                window.location.reload()
              }
            })
            break;
        }
      }
    };

    wb.register();
  }

  // The trees of the queue are kept in the browser's storage, which can be reclaimed when the device runs
  // short of space - and, on iOS, deleted after 7 days without a visit when the app is not installed.
  // Asking for "persistent" storage takes the origin out of that pool where the browser supports it:
  // Chromium grants it silently to a site that is installed or that the user engages with, Firefox asks
  // the user, and Safari has no `persist()` at all. A refusal is the normal case, so the answer is only
  // reported: the debug panel shows `navigator.storage.persisted()`.
  if (navigator.storage?.persist) {
    navigator.storage.persist()
      .then((granted) => console.debug('TreeWallet: persistent storage', granted ? 'granted' : 'not granted'))
      .catch((error) => console.debug('TreeWallet: could not request persistent storage', error));
  }

  const originalConsoleError = window.console.error;
  const originalConsoleLog = window.console.log;
  const originalConsoleInfo = window.console.info;
  window.console.error = (...args) => {
    onError(args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : arg).join(' '), 'error');
    originalConsoleError(...args);
  };

  window.console.log = (...args) => {
    onError(args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : arg).join(' '), 'log');
    originalConsoleLog(...args);
  };

  window.console.info = (...args) => {
    onError(args.map(arg => typeof arg === 'object' ? JSON.stringify(arg) : arg).join(' '), 'info');
    originalConsoleInfo(...args);
  };

  window.document.addEventListener("error", (event) => {
    console.error('caught error', event.message);
    onError(event.message, 'error');
  });

  window.document.addEventListener("DOMContentLoaded", async () => {
    const [user, contracts] = await Promise.all([
      UserRepository.getUser()
        .then(async () => {
          try {
            if (!ConfigurationService.isCurrentVersionInstalled()) {
              await UserRepository.getUser({
                successCallback: async (user: User) => {
                  if (user) {
                    AuthService.setUser(user);
                  }
                },
                errorCallback: (error: Error) => {
                  console.error('there was an error fetching user data', error)
                },
              });
            }
          } catch (err) {
            console.error('there was an unknown error', err)
          }
        }),
      ContractRepository.getContractsList()
    ]);

    /*
    if (process.env.NODE_ENV === 'production') {
      const _paq = window._paq = window._paq || [];
      // tracker methods like "setCustomDimension" should be called before "trackPageView"
      _paq.push(['trackPageView']);
      _paq.push(['enableLinkTracking']);
      (function () {
        const u = "//analytics.terencepires.com/";
        _paq.push(['setTrackerUrl', u + 'matomo.php']);
        _paq.push(['setSiteId', '1']);
        if (AuthService.getUser()) {
          const user = AuthService.getUser();
          _paq.push(['setUserId', user.email]);
        }
        const d = document;
        const g = d.createElement('script')
        const s = d.getElementsByTagName('script')[0];
        g.async = true;
        g.src = u + 'matomo.js';
        s.parentNode?.insertBefore(g, s);
      })();
    }
    */

    const element = document.getElementById('root');
    if (element) {
      const root = ReactDOMClient.createRoot(element);
      root.render(
        <BrowserRouter>
          {/*<React.StrictMode>*/}
          <App user={user} contracts={contracts} />
          {/*</React.StrictMode>*/}
        </BrowserRouter>,
      );
    }
  });
} catch (err) {
  console.error('there was an error', err)
}
